import { useResendEmailVerification } from "@/api";
import Alert from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import { toast } from "@/components/ui/use-toast";
import { ApolloError } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { GraphQLError } from "graphql";
import { Mail } from "lucide-react";
import { useLocalStorage } from "usehooks-ts";

const USER_ID_REGEX = /UserID: "([A-z0-9-_]+\|[a-zA-Z0-9-_]+)"/;

interface Props {
  errorMsg: string;
}

/**
 * shows the unverified email error page
 * extracts userid from errorMsg and displays a resend email button if userid is present
 *
 * @example
 * const errorMsg = 'email not verified. User: "auth0|123abc"'
 * <UnverifiedEmail errorMsg={errorMsg} />
 */
const UnverifiedEmail = ({ errorMsg }: Props) => {
  const userId = errorMsg.match(USER_ID_REGEX)?.[1];
  const { logout } = useAuth0();
  const [resendEmailVerification, { loading }] = useResendEmailVerification();
  const [, setLatestOrganizationSlug] = useLocalStorage<string | null>(
    "latestOrganizationSlug",
    null
  );

  const refreshPage = () => {
    const { protocol, host, pathname } = window.location;
    window.location.replace(`${protocol}//${host}${pathname}`);
  };

  const handleBack = () => {
    setLatestOrganizationSlug(null);
    logout();
  };

  const handleResendEmailVerification = async () => {
    if (!userId) {
      console.error(
        "No user ID. A user id is required to resend a verification email. Please contact support if this occurs again."
      );
      return undefined;
    }

    try {
      const res = await resendEmailVerification({
        variables: { userId }
      });

      if (res.errors && res.errors.length > 0) {
        console.error(res.errors);
        return defaultErrorToast(
          res.errors.map((e) => `"${e.message}"`).join(", ")
        );
      }

      switch (res.data?.resendEmailVerification.__typename) {
        case "ResendEmailVerificationSuccess":
          return toast({
            title: "Verification email has been sent!",
            description:
              "A verification email has been sent to your email address. Please verify your email to continue."
          });
        case "UserNotFound":
          return toast({
            title: "User not found",
            description:
              "We couldn't find a user with that ID. Please contact support.",
            variant: "destructive"
          });
        default:
          return defaultErrorToast("Unknown typename");
      }
    } catch (err) {
      let errString: string = "";
      if (
        err instanceof Error ||
        err instanceof GraphQLError ||
        err instanceof ApolloError
      )
        errString = err.message;

      console.error(err);
      return defaultErrorToast(errString);
    }
  };

  if (!userId) {
    return (
      <div className="flex w-full h-screen items-center justify-center">
        <Alert
          className="min-w-56 max-w-lg"
          title="Could not find user"
          description={
            <div className="relative w-full">
              <p>Please contact support.</p>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={handleBack}>
                  Logout
                </Button>
              </div>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex w-full h-screen items-center justify-center">
      <Alert
        className="min-w-56 max-w-lg"
        title="Email not verified"
        disableReport
        description={
          <div className="relative w-full">
            <p>To access the platform, please verify your email.</p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={handleBack}>
                Logout
              </Button>
              <Button onClick={refreshPage}>Refresh</Button>
              <Button onClick={handleResendEmailVerification}>
                Resend Verification Email
                <span className="ml-4">
                  {loading ? (
                    <LoadingCircle className="h-8 w-8" />
                  ) : (
                    <Mail size={16} />
                  )}
                </span>
              </Button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default UnverifiedEmail;

function defaultErrorToast(err?: string) {
  return toast({
    title: "Error resending verification email",
    description: (
      <>
        An error occurred while resending the verification email. Please try
        again later or contact support.
        {err && (
          <>
            <br />
            <>Error: {err}</>
          </>
        )}
      </>
    ),
    variant: "destructive"
  });
}
